import type { Environment } from '@core/entities/Environment';
import type {
  EnvironmentDocumentPayload,
  EnvironmentRepository,
} from '@core/repositories/EnvironmentRepository';
import { httpClient } from '@infrastructure/http/httpClient';
import { toAppError } from '@infrastructure/http/errorMapper';
import {
  toVariable,
  toVariableInputPayload,
  type VariableResponse,
} from '@infrastructure/mappers/toVariable';

interface EnvironmentResponse {
  id: string;
  workspace_id: string;
  name: string;
  is_active: boolean;
  created_by?: { id: string; name: string } | null;
  variables?: VariableResponse[];
  variable_count: number | null;
  abilities: { view: boolean; update: boolean; delete: boolean };
  created_at: string;
  updated_at: string;
}

const toEnvironment = (payload: EnvironmentResponse): Environment => ({
  id: payload.id,
  workspaceId: payload.workspace_id,
  name: payload.name,
  isActive: payload.is_active,
  createdBy: payload.created_by ?? null,
  variables: (payload.variables ?? []).map(toVariable),
  variableCount: payload.variable_count,
  abilities: payload.abilities,
  createdAt: payload.created_at,
  updatedAt: payload.updated_at,
});

const toDocumentBody = (payload: EnvironmentDocumentPayload) => ({
  name: payload.name,
  variables: (payload.variables ?? []).map(toVariableInputPayload),
});

export class EnvironmentRepositoryImpl implements EnvironmentRepository {
  async list(workspaceId: string): Promise<Environment[]> {
    try {
      const response = await httpClient.get<{ data: EnvironmentResponse[] }>(
        `/workspaces/${workspaceId}/environments`,
      );
      return response.data.data.map(toEnvironment);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async get(id: string): Promise<Environment> {
    try {
      const response = await httpClient.get<{ data: EnvironmentResponse }>(`/environments/${id}`);
      return toEnvironment(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async create(workspaceId: string, payload: EnvironmentDocumentPayload): Promise<Environment> {
    try {
      const response = await httpClient.post<{ data: EnvironmentResponse }>(
        `/workspaces/${workspaceId}/environments`,
        toDocumentBody(payload),
      );
      return toEnvironment(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async update(id: string, payload: EnvironmentDocumentPayload): Promise<Environment> {
    try {
      const response = await httpClient.put<{ data: EnvironmentResponse }>(
        `/environments/${id}`,
        toDocumentBody(payload),
      );
      return toEnvironment(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async activate(id: string): Promise<Environment> {
    try {
      const response = await httpClient.post<{ data: EnvironmentResponse }>(
        `/environments/${id}/activate`,
      );
      return toEnvironment(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async remove(id: string): Promise<void> {
    try {
      await httpClient.delete(`/environments/${id}`);
    } catch (error) {
      throw toAppError(error);
    }
  }
}
